import { useEffect, useState } from "react";
import {
  debriefConfirmAttendance,
  debriefDeclineAttendance,
  debriefOpen,
  debriefSetAlignment,
  debriefSubmitWrittenInput,
  type DebriefOpenOutput,
} from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { STAGE_LABEL } from "@/ui/vocabulary";
import { Banner, Empty, Pill, ReasonDialog, Receipt, fmtDate } from "@/ui";
import { WrittenInputDialog } from "@/views/debriefs/WrittenInputDialog";
import { ANSWER_SAID, ATTENDANCE_TONE, offeredOf, recoLabel, recoTone, STATUS_LABEL, STATUS_TONE } from "@/views/debriefs/vocabulary";

// V1-INTERACTIONS §4.15 — the interviewer's own debrief (EM-18…EM-20; AF-20, AF-21, AF-22, AF-23). The room the
// Recruiter facilitates is a different screen from the room an interviewer attends: theirs is their attendance, their
// own scorecard and their own alignment, and nothing about any other interviewer. It is a separate component rather
// than a seat test threaded through the facilitator's room, so a control can never be added to one and leak into the
// other. The server serves the same narrowing (`debrief.open` → `projection: "interviewer"`), so the descoping is on
// the wire and not only on the screen.

type Member = DebriefOpenOutput["members"][number];

export function InterviewerRoom({ rid, cid }: { rid: string; cid: string }) {
  const { register } = useViewState();
  const { me, can } = useSession();
  const q = useRead(["debrief", cid, rid], () => debriefOpen({ requisition_id: rid, cycle_id: cid }));
  useEffect(() => register({ view: "debrief", ids: { requisition: rid, cycle: cid }, summary: `debrief ${q.data?.status ?? ""}` }), [register, rid, cid, q.data?.status]);
  const inval = [["debrief", cid], ["debriefs"], ["today"]] as const;
  const confirm = useWrite((i: { requisition_id: string; cycle_id: string }, k) => debriefConfirmAttendance(i, k), inval);
  const decline = useWrite((i: { requisition_id: string; cycle_id: string; reason: string }, k) => debriefDeclineAttendance(i, k), inval);
  const align = useWrite((i: Parameters<typeof debriefSetAlignment>[0], k) => debriefSetAlignment(i, k), inval);
  const written = useWrite((i: Parameters<typeof debriefSubmitWrittenInput>[0], k) => debriefSubmitWrittenInput(i, k), inval);
  const [dlg, setDlg] = useState<null | { kind: "decline" } | { kind: "written"; aid: string; candidate: string }>(null);
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  const c = q.data;
  const err = confirm.error ?? decline.error ?? align.error ?? written.error;
  const revealed = c.status === "revealed" || c.status === "decided" || c.status === "closed";
  // the room returns this seat's own row and no other: whether they replied, and what the roll said if it has been taken
  const mine = c.required_attendees.find((a) => String(a["person_id"]) === me?.person_id);
  const reply = mine ? String(mine["status"] ?? "") : "";
  const iDeclined = reply === "declined";
  const attendance = mine ? String(mine["attendance_label"] ?? "Required") : "Required";
  const scheduled = c.status === "scheduled";
  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">
            Debrief #{c.sequence} · {c.requisition_title}
          </h1>
          <p className="lede">
            <a href="#debriefs">← my debriefs</a> · facilitator {c.facilitator}
            {c.booking ? ` · ${fmtDate(String(c.booking["start"]), true)}` : " · not scheduled"}
          </p>
        </div>
        <div className="acts">
          <Pill tone={STATUS_TONE[c.status] ?? "muted"}>{STATUS_LABEL[c.status] ?? c.status}</Pill>
        </div>
      </div>
      <Banner error={err} />
      {confirm.isSuccess && <Receipt>Recorded — you are down as attending. The Recruiter takes the roll in the room.</Receipt>}
      {decline.isSuccess && <Receipt>Recorded — the Recruiter and the Hiring Manager have been told. Send your written input before the debrief starts.</Receipt>}
      {written.isSuccess && <Receipt>Sent — the Recruiter reads it into the room.</Receipt>}
      <section className="card">
        <h2 className="sec">Your attendance</h2>
        <p>
          <Pill tone={ATTENDANCE_TONE[attendance] ?? "muted"}>{attendance}</Pill>{" "}
          <span className="muted">
            {attendance === "Required"
              ? `Everyone on the panel is required until the Recruiter takes the roll.${reply ? ` You replied: ${ANSWER_SAID[reply] ?? reply}.` : ""}`
              : "Taken from the roll in the room — this is what actually happened."}
          </span>
        </p>
        {/* F-85: the reply this seat gave is pressed and drawn as the selected button; the other stays offered so the
            answer can be changed (the same pair as My Debriefs' ReplyButtons, kept here so the room imports no page) */}
        {scheduled && can("debrief.confirm_attendance") && (
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <button type="button" className={reply === "confirmed" ? "btn btn--primary" : "btn"} aria-pressed={reply === "confirmed"} disabled={confirm.isPending} onClick={() => void confirm.mutateAsync({ requisition_id: rid, cycle_id: cid })}>
              Attend
            </button>
            <button type="button" className={iDeclined ? "btn btn--primary" : "btn"} aria-pressed={iDeclined} onClick={() => setDlg({ kind: "decline" })}>
              I cannot attend
            </button>
          </div>
        )}
        {iDeclined && <p className="muted">You said you cannot attend. Your written input is due before the debrief starts and is read into the room; the debrief is not rescheduled.</p>}
      </section>
      {c.members.length === 0 && <Empty level={2} heading="No candidate on this debrief">The Recruiter sets the cycle's membership when the room is created.</Empty>}
      {c.members.map((m) => (
        <CandidateCard
          key={m.application_id}
          m={m}
          revealed={revealed}
          offered={offeredOf(c)}
          canWrite={iDeclined && !revealed && can("debrief.submit_written_input")}
          busy={align.isPending}
          onAlign={(value) => void align.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: m.application_id, value: value as Parameters<typeof debriefSetAlignment>[0]["value"] })}
          onWritten={() => setDlg({ kind: "written", aid: m.application_id, candidate: m.candidate })}
        />
      ))}
      <ReasonDialog title="I cannot attend" label="Why" confirm="Decline" open={dlg?.kind === "decline"} onClose={() => setDlg(null)} busy={decline.isPending} error={decline.error} onConfirm={(reason) => void decline.mutateAsync({ requisition_id: rid, cycle_id: cid, reason }).then(() => setDlg(null))}>
        <p className="muted">Your written input (what you would have said, and optionally your alignment) is due before the debrief starts and is read into the room.</p>
      </ReasonDialog>
      <WrittenInputDialog
        open={dlg?.kind === "written"}
        rid={rid}
        offered={offeredOf(c)}
        candidate={dlg?.kind === "written" ? dlg.candidate : ""}
        onClose={() => setDlg(null)}
        busy={written.isPending}
        error={written.error}
        onSubmit={(v) => dlg?.kind === "written" && void written.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, text: v.text, file_id: v.fileId, ...(v.alignment ? { alignment: v.alignment as NonNullable<Parameters<typeof debriefSubmitWrittenInput>[0]["alignment"]> } : {}) }).then(() => setDlg(null))}
      />
    </div>
  );
}

// AF-23 (CALL-17 29:36–30:31): one card per candidate carrying the two things the interviewer does here — the status of
// their OWN scorecard with the way back into it, and, after the reveal, their OWN alignment. No sheet, no matrix, no
// divergence, no other author's row: the server does not send them (POLICY §8.5) and this screen has nowhere to put them.
function CandidateCard({ m, revealed, offered, canWrite, busy, onAlign, onWritten }: { m: Member; revealed: boolean; offered: readonly string[]; canWrite: boolean; busy: boolean; onAlign: (v: string) => void; onWritten: () => void }) {
  const card = m.my_scorecard;
  const mine = m.alignments.find((a) => a["mine"]);
  const submitted = !!card?.["submitted"];
  const packet = card ? String(card["packet_id"] ?? "") : "";
  return (
    <section className="card">
      <div className="hrow">
        <h2 className="sec">
          {m.candidate} <Pill category tone="muted">{STAGE_LABEL[m.stage] ?? m.stage}</Pill>
          {m.withdrawn ? <Pill tone="bad">withdrawn</Pill> : null}
        </h2>
        <div className="acts">
          {canWrite && mine && (
            <button className="btn btn--sm btn--primary" onClick={onWritten}>
              Written input
            </button>
          )}
        </div>
      </div>
      <h3 className="sec">Your scorecard</h3>
      {!card && <p className="muted">You hold no round on this candidate.</p>}
      {card && !submitted && (
        <p>
          <Pill tone="warn">You have not submitted your scorecard</Pill>{" "}
          <span className="muted">{String(card["round"])} · it is due before the debrief.</span>
        </p>
      )}
      {card && submitted && (
        <p>
          <Pill tone="ok">Submitted</Pill>{" "}
          <span className="muted">
            {String(card["round"])}
            {card["core_average"] ? ` · ${String(card["core_average"])} / 5` : card["average"] && !("core_average" in card) ? ` · ${String(card["average"])} / 5` : ""}
            {card["recommendation"] ? ` · ${recoLabel(card["recommendation"]) ?? String(card["recommendation"])}` : ""}
          </span>
        </p>
      )}
      {card && card["reservation"] ? <blockquote className="prose">{String(card["reservation"])}</blockquote> : null}
      {packet && (
        <a className="btn btn--sm" href={`#packets/${packet}/${m.application_id}`}>
          {submitted ? "Revisit your scorecard" : "Open your scorecard"}
        </a>
      )}
      {revealed && mine && (
        <fieldset className="decision-group">
          <legend>Your alignment</legend>
          {/* AF-22 removes the Alignment section — the roll of everyone's values. V1-INTERACTIONS §4.15 item 5 still
              obliges the room to offer this person their OWN row, and the outcome gate reads it, so their row stays and
              every other row is gone. POLICY §6.2: it opens empty and is never pre-filled from their recommendation. */}
          {m.can["set_alignment"] ? (
            <fieldset className="radios" disabled={!!mine["bound_no_hire"]}>
              <legend className="sr-only">Your alignment on {m.candidate}</legend>
              {offered.map((v) => (
                <label key={v} className={`opt${(mine["value"] ?? "") === v ? " opt--on" : ""}`}>
                  <input type="radio" name={`al-${m.application_id}`} checked={(mine["value"] ?? "") === v} disabled={busy} onChange={() => onAlign(v)} /> {recoLabel(v)}
                </label>
              ))}
            </fieldset>
          ) : (
            <Pill tone={recoTone(mine["value"])}>{mine["value"] ? recoLabel(mine["value"]) ?? String(mine["value"]) : "not set"}</Pill>
          )}
          {mine["bound_no_hire"] ? <p className="muted">Bound by the two-2s rule.</p> : null}
          {mine["written_input"] ? (
            <p className="muted">
              {String(mine["written_input_label"] ?? "written input — not an alignment")}
              {mine["written_input_says"] ? ` · you would have said ${recoLabel(mine["written_input_says"]) ?? String(mine["written_input_says"])}` : ""}
            </p>
          ) : null}
        </fieldset>
      )}
    </section>
  );
}
